import { select } from 'd3-selection';
import { curveCatmullRom, curveLinear, curveMonotoneX, curveNatural, curveStep, curveStepAfter, curveStepBefore } from 'd3-shape';

import { v4 as uuid } from 'uuid';

import { createBlob, createObjectURL, revokeObjectURL } from 'blob-util';
import { saveAs } from 'file-saver';

import { getTimeFieldName } from '@23degrees/shared-code/config/dimension.config';
import { Fields } from '@23degrees/shared-code/domain/content/entities/valueobjects/Fields';
import { Content } from '@23degrees/shared-code/models';
import { objToMoment } from '@23degrees/shared-code/utilities/date';

export { uuid };

export const toMoment = (obj, content: Content) => {
  const timeField = getTimeFieldName(content?.typeSpecific?.fields as Fields);
  const { format } = content.typeSpecific.fields[timeField];
  const { inputFormat } = format.date;
  const date = objToMoment(obj[timeField], inputFormat, format.locale);
  return date;
};

export const createSVG = (id: string, domElement: HTMLDivElement) => {
  const element = select(domElement).append('svg').attr('class', id);
  return { id: id, width: domElement.clientWidth, height: domElement.clientHeight, element: element };
};

export const createOverlay = (id: string, domElement: HTMLDivElement) => {
  const element = select(domElement).append('div').attr('class', id);
  return { id: id, width: domElement.clientWidth, height: domElement.clientHeight, element: element };
};

export const updateSVG = (obj: any, width: number, height: number) => {
  obj.element.attr('width', width).attr('height', height);
  obj.width = width;
  obj.height = height;
};

export const updateOverlay = (obj: any, width: number, height: number) => {
  obj.element.style('width', `${width}px`).style('height', `${height}px`);
  obj.width = width;
  obj.height = height;
};

export const exportSVG = (obj: any) => {
  const replaceSpecial = (str) => str.replace(/&nbsp;/g, ' ');

  let height = 0;
  let groups = ['header', 'legend', 'chart']; // 'footer'

  const { typeSpecific } = obj.content as Content;
  if (!typeSpecific.chart.legend.enabled) {
    groups = groups.filter((groupObj) => groupObj !== 'legend');
  }

  const div = obj.document.createElement('div');
  const svg = select(div).append('svg').attr('title', obj.content.fileName).attr('version', 1.1).attr('xmlns', 'http://www.w3.org/2000/svg');

  // linear-gradient(to top, rgb(207, 217, 223) 0%, rgb(226, 235, 240) 100%)
  // linear-gradient(120deg, rgb(132, 250, 176) 0%, rgb(143, 211, 244) 100%)
  // radial-gradient(at 50% 100%, rgba(255, 255, 255, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), linear-gradient(rgba(255, 255, 255, 0.25) 0%, rgba(0, 0, 0, 0.25) 100%)

  // const fillStyle = svg
  //   .append('linearGradient')
  //   .attr('x1', 0)
  //   .attr('y1', 0)
  //   .attr('x2', '100%')
  //   .attr('y2', '100%')
  //   .attr('id', 'background');
  //
  // fillStyle
  //   .append('stop')
  //   .attr('stop-color', '#000')
  //   .attr('offset', 0);
  //
  // fillStyle
  //   .append('stop')
  //   .attr('stop-color', '#fff')
  //   .attr('offset', '100%');

  const { backgroundColor } = typeSpecific.style.background;

  // .attr('fill', 'url(#background)');
  const background = svg
    .append('g')
    .attr('class', 'background')
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', obj.chart.width)
    .attr('height', 0)
    .attr('fill', backgroundColor);

  for (const group of groups) {
    if (!obj[group] || !obj[group].element) continue;

    const objNode: any = svg
      .append('g')
      .attr('class', group)
      .attr('transform', 'translate(0, ' + height + ')')
      .node();

    const groupNode: any = obj[group].element.node();
    for (const child of groupNode.children) {
      if (child.classList.contains('voronoi')) continue;
      objNode.appendChild(child.cloneNode(true));
    }

    height += Number.parseFloat(obj[group].height);
  }

  background.attr('height', height);

  // footer
  // <image width="100" height="100" xlink:href="data:image/png;base64,...">

  // svg
  svg.attr('width', obj.chart.width + 'px').attr('height', height + 'px');
  const svgNode: any = svg.node();
  const html = replaceSpecial(svgNode.parentNode.innerHTML);
  const blob = createBlob([html], { type: 'image/svg+xml' });

  const fileName = obj.content.slug + '.svg';
  saveAs(blob, fileName);

  return { blob, width: obj.chart.width, height };
};

export const blobToBase64 = (blob, callback) => {
  const reader = new FileReader();
  reader.onload = () => callback(reader.result);
  reader.readAsDataURL(blob); // converts the blob to base64 and calls onload
};

export const exportPNG = (blob: any, callback: any, document: any, width: number, height: number) => {
  // const base64Image = 'data:image/svg+xml;base64,' + await blobToBase64String(blob); // btoa(unescape(encodeURIComponent(html)));
  // const base64Image = new Buffer(blob, 'binary').toString('base64');

  blobToBase64(blob, (base64Image) => {
    const imgSrc = base64Image;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    const image = new Image();
    image.onload = () => {
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      canvas.toBlob((canvasBlob) => callback(canvasBlob));
    };

    image.src = imgSrc;
  });
};

export const setupDownloadFile = (document) => {
  // return (blob, name) => saveAs(blob, name);
  return (blob, name) => {
    // @ts-ignore
    // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    //   // If IE, you must uses a different method.
    //    // @ts-ignore
    //   window.navigator.msSaveOrOpenBlob(blob, name);
    // } else {
    const blobURL = createObjectURL(blob);
    // window.open(blobURL, '_blank');
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = blobURL;
    a.target = '_blank';
    a.download = name;
    a.click();
    revokeObjectURL(blobURL);
    document.body.removeChild(a);
    // }
  };
};

export const getCurve = (type: string): any => {
  // TODO remove constant strings with config
  if (type === 'monotone') return curveMonotoneX;
  if (type === 'natural') return curveNatural;
  if (type === 'step') return curveStep;
  if (type === 'stepBefore') return curveStepBefore;
  if (type === 'stepAfter') return curveStepAfter;
  if (type === 'catmullRom') return curveCatmullRom;

  return curveLinear;
};

export const getCSSFont = (obj: any): string => {
  const fontStyle = obj.fontStyle || 'normal'; // normal | italic
  const fontVariant = obj.fontVariant || 'normal'; // normal | small-caps
  const fontWeight = String(obj.weight).replace('i', '') || 'normal'; // normal | bold | bolder | lighter | number
  const fontSize = (obj.size || 13) + 'px';
  const lineHeight = obj.lineHeight || 'normal'; // normal || number
  const fontFamily = obj.fontFamily || `'Roboto', sans-serif`;

  // font: font-style font-variant font-weight font-size/line-height font-family;
  return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize}/${lineHeight} ${fontFamily}`;
};

// https://gist.github.com/talltyler/5345894
export const setupGetTextMetrics = (document: any) => {
  // cache canvas & context
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  // return function
  return (text: string, styleObj: any): TextMetrics => {
    context.font = getCSSFont(styleObj);
    const metrics = context.measureText(text);
    return metrics;
  };
};

export const setupTrimTextToWidth = (getTextMetrics: any) => {
  return (text: string, width: number, styleObj: any): string => {
    text = String(text);
    const textWidth = getTextMetrics(text, styleObj).width;
    if (!width || textWidth < width) return text;

    const ret = [];
    for (const char of text.split('')) {
      const curText = (ret.join('') + char).trim();
      const curWidth = getTextMetrics(curText, styleObj).width;

      if (curWidth > width) {
        return curText.substr(0, curText.length - 3).trim() + '...';
      }
      ret.push(char);
    }

    return ret.join('');
  };
};

export const setupSplitTextToLines = (getTextMetrics: any) => {
  return (text: string, lines: number, styleObj: any): any => {
    text = String(text);
    const textWidth = getTextMetrics(text, styleObj).width;
    const partWidth = textWidth / lines;

    if (lines < 2) return { lines: [text], maxWidth: textWidth };

    const matches = text.match(/([^\s-.:;&]+[\s-.:;&]*)/g);
    for (const [idx, piece] of matches.entries()) {
      const split = piece.split('/');
      const pieces = [split.shift()];

      for (const word of split) {
        if (word.length < 3) {
          pieces[pieces.length - 1] += '/' + word;
        } else {
          pieces.push(word);
        }
      }

      matches.splice(idx, 1, ...pieces);
    }

    let line = [];
    const greedyRet = { lines: [], maxWidth: 0 };
    const notGreedyRet = { lines: [], maxWidth: 0 };

    // greedy
    for (const piece of matches) {
      line.push(piece);
      const curLine = line.join('').trim();
      const lineWidth = getTextMetrics(curLine, styleObj).width;

      if (lineWidth > partWidth && greedyRet.lines.length + 1 < lines) {
        greedyRet.lines.push(curLine);
        greedyRet.maxWidth = Math.max(greedyRet.maxWidth, lineWidth);
        line = [];
      }
    }

    if (line.length > 0) {
      const lastLine = line.join('').trim();
      const lastWidth = getTextMetrics(lastLine, styleObj).width;
      greedyRet.lines.push(lastLine);
      greedyRet.maxWidth = Math.max(greedyRet.maxWidth, lastWidth);
      line = [];
    }

    // not greedy
    line.push(matches.shift());
    for (const piece of matches) {
      const curText = (line.join('') + piece).trim();
      const curWidth = getTextMetrics(curText, styleObj).width;

      if (curWidth > partWidth && notGreedyRet.lines.length + 1 < lines) {
        const curLine = line.join('').trim();
        const lineWidth = getTextMetrics(curLine, styleObj).width;
        notGreedyRet.lines.push(curLine);
        notGreedyRet.maxWidth = Math.max(notGreedyRet.maxWidth, lineWidth);
        line = [];
      }

      line.push(piece);
    }

    if (line.length > 0) {
      const lastLine = line.join('').trim();
      const lastWidth = getTextMetrics(lastLine, styleObj).width;
      notGreedyRet.lines.push(lastLine);
      notGreedyRet.maxWidth = Math.max(notGreedyRet.maxWidth, lastWidth);
      line = [];
    }

    return greedyRet.maxWidth <= notGreedyRet.maxWidth ? greedyRet : notGreedyRet;
  };
};

export const setupSplitTextToWidth = (getTextMetrics: any) => {
  return (text: string, width: number, styleObj: any): any => {
    text = String(text);
    const textWidth = getTextMetrics(text, styleObj).width;
    if (!width || textWidth < width) return { lines: [text], maxWidth: textWidth };

    const minLines = Math.ceil(textWidth / width);
    const partWidth = textWidth / minLines;

    let line = [];
    const ret = { lines: [], maxWidth: 0 };

    const matches = text.match(/([^\s-.:;&]+[\s-.:;&]*)/g);
    for (const [idx, piece] of matches.entries()) {
      const split = piece.split('/');
      const pieces = [split.shift()];

      for (const word of split) {
        if (word.length < 3) {
          pieces[pieces.length - 1] += '/' + word;
        } else {
          pieces.push(word);
        }
      }

      matches.splice(idx, 1, ...pieces);
    }

    for (const [idx, piece] of matches.entries()) {
      const curText = (line.join('') + piece).trim();
      const curWidth = getTextMetrics(curText, styleObj).width;
      const equalSpread = (idx + 1) / matches.length >= (ret.lines.length + 1) / minLines;

      if ((curWidth > width || curWidth > partWidth * 1.2) && line.length > 0) {
        const curLine = line.join('');
        const lineWidth = getTextMetrics(curLine, styleObj).width;
        ret.lines.push(curLine);
        ret.maxWidth = Math.max(ret.maxWidth, lineWidth);
        line = [];
      }

      line.push(piece);
    }

    if (line.length > 0) {
      const lastLine = line.join('');
      const lastWidth = getTextMetrics(lastLine, styleObj).width;
      ret.lines.push(lastLine);
      ret.maxWidth = Math.max(ret.maxWidth, lastWidth);
    }

    ret.lines = ret.lines.filter((obj) => obj.length > 0);

    return ret;
  };
};

type CornerRadius = {
  topLeft?: number;
  topRight?: number;
  bottomRight?: number;
  bottomLeft?: number;
};

export const rectToPath = (x: number, y: number, width: number, height: number, cornerRadius: CornerRadius = {}): string => {
  const { topLeft = 0, topRight = 0, bottomRight = 0, bottomLeft = 0 } = cornerRadius;

  // Limit radii to half the smallest side to prevent overlap
  const maxRadius = Math.min(width, height) / 2;
  const tl = Math.min(topLeft, maxRadius);
  const tr = Math.min(topRight, maxRadius);
  const br = Math.min(bottomRight, maxRadius);
  const bl = Math.min(bottomLeft, maxRadius);

  // Start building the path
  return `
    M ${x + tl},${y}
    H ${x + width - tr}
    ${tr >= 0 ? `A ${tr},${tr} 0 0 1 ${x + width},${y + tr}` : ''}
    V ${y + height - br}
    ${br >= 0 ? `A ${br},${br} 0 0 1 ${x + width - br},${y + height}` : ''}
    H ${x + bl}
    ${bl >= 0 ? `A ${bl},${bl} 0 0 1 ${x},${y + height - bl}` : ''}
    V ${y + tl}
    ${tl >= 0 ? `A ${tl},${tl} 0 0 1 ${x + tl},${y}` : ''}
    Z`
    .replace(/\s+/g, ' ')
    .trim();
};
